import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Circle, Loader2, Users } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { initials } from "@/lib/format";
import { usePresence } from "@/hooks/usePresence";

interface OnlineUser {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  avatar_url: string;
}

// ---------------------------------------------------------------------------
// OnlineUsersBadge — small pill showing "🟢 N online" that opens a
// dialog listing every signed-in user when clicked.
//
// The count comes from usePresence (driven by the WS deltas, so it's
// live without polling). The full user list is only fetched when the
// dialog opens — keeps the badge cheap and lets us reuse the existing
// /api/v1/presence/users/ join only when the user actually wants to
// see who's around.
// ---------------------------------------------------------------------------
export function OnlineUsersBadge({
  /** Tighter variant for the thread-detail header bar. */
  compact = false,
}: {
  compact?: boolean;
} = {}) {
  const [open, setOpen] = useState(false);
  const { onlineCount } = usePresence();

  // Only enabled while the dialog is open — no background polling.
  const { data, isFetching } = useQuery<{ count: number; results: OnlineUser[] }>({
    enabled: open,
    queryKey: ["presence", "users"],
    queryFn: async () => {
      const { data } = await api.get<{ count: number; results: OnlineUser[] }>(
        "/presence/users/",
      );
      return data;
    },
    // Refetch when re-opened — the WS deltas may have changed the set.
    staleTime: 5_000,
  });

  return (
    <>
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        onClick={() => setOpen(true)}
        className="gap-2"
        title="See who's online"
        aria-label={`${onlineCount} ${onlineCount === 1 ? "person" : "people"} online — click to view`}
      >
        <span className="relative inline-flex">
          <Circle className="h-2.5 w-2.5 fill-emerald-500 text-emerald-500" />
          <span className="absolute inset-0 inline-flex animate-ping rounded-full bg-emerald-500 opacity-60" />
        </span>
        <span className="tabular-nums font-medium">{onlineCount}</span>
        {!compact && <span className="text-muted-foreground">online</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {onlineCount} {onlineCount === 1 ? "person" : "people"} online
            </DialogTitle>
          </DialogHeader>

          {isFetching && !data ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : !data || data.results.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No-one else is online right now.
            </div>
          ) : (
            <ul className="max-h-[60vh] space-y-1 overflow-y-auto pr-1">
              {data.results.map((u) => (
                <li
                  key={u.id}
                  className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-accent/50"
                >
                  <span className="relative shrink-0">
                    <Avatar className="h-9 w-9 ring-1 ring-border">
                      {u.avatar_url && <AvatarImage src={u.avatar_url} alt="" />}
                      <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-xs font-semibold text-primary-foreground">
                        {initials(u.full_name || u.email)}
                      </AvatarFallback>
                    </Avatar>
                    {/* Reuses the same green dot the PresenceAvatar wears. */}
                    <span
                      className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-card"
                      aria-label="Online"
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">
                      {u.full_name || u.email}
                    </div>
                    {u.full_name && (
                      <div className="truncate text-xs text-muted-foreground">
                        {u.email}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
